/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { PropertyType } from './property-type.enum';
import { NameValue } from '../tool/name-value';
import { Size } from '../enum/column-size.enum';
import { MockEntity } from './mock-entity';
import { Mask } from '../enum/mask.enum';
import { Validation } from './validation/validation';
import { Alias } from '../enum/alias.enum';
import { Relation } from './relation/relation';
import { RelationType } from './relation/relation-type.enum';
import { DetailsViewType } from '../enum/details-view-type.enum';
import { DataModelFilters } from './data-model-filters.enum';
import { DetailsOptions } from '../model/search/details-options';
import { TableFilterType } from '../enum/table-filter-type.enum';
import { StandardAction } from '../tool/action/standard-action';
import { HttpFilter } from './http/http-filter';
import { TvaSettings } from '../model/input/tva-settings';
import { IEntity } from '../interface/IEntity';
import { Observable } from 'rxjs';
import { SortType } from '../enum/sort-type';
import { CommonUtils } from '../tool/common-utils';

export class ModelProperty {
  get dateMax(): Date {
    return this._dateMax;
  }
  set dateMax(value: Date) {
    if (value instanceof Date) {
      this._dateMax = value;
    } else if (value) {
      this._dateMax = CommonUtils.convertStrToDate(value);
    }
  }
  get dateMin(): Date {
    return this._dateMin;
  }
  set dateMin(value: Date) {
    if (value instanceof Date) {
      this._dateMin = value;
    } else if (value) {
      this._dateMin = CommonUtils.convertStrToDate(value);
    }
  }

  public trackId = CommonUtils.uuid();

  // General
  public id: string;
  public name: string;
  public title: string;
  public type: PropertyType;
  public format: string;
  public displayId = true;
  public displayLabel = true;
  public primaryKey: boolean;
  public mainLabel: boolean;
  public properties: any;
  public field: string; // Set and get value on other field
  public tradKey = '';

  public icon: string; // Font-awesome Icon

  // input number
  public showArrow = false;
  public withDecimal = true;
  public decimal: number;
  public mode: 'multi' | 'mono' = 'mono';

  // textarea
  public maxHeight: number;

  // Datatable
  public align = 'left';
  public sortable = true;
  public sortByField: string | undefined;

  get sortType(): SortType {
    if (!this._sortType) {
      switch (this.editType) {
        case PropertyType.date:
          return SortType.date;
        case PropertyType.dateTime:
          return SortType.dateTime;
        default:
          return SortType.string;
      }
    }
    return this._sortType;
  }

  set sortType(value: SortType) {
    this._sortType = value;
  }

  private _sortType: SortType;
  public anchorByDefault = false;
  public groupable = true;
  public resizable = true;
  public selectable = true;
  public defaultSort: boolean;
  public defaultSortOrder: 'asc' | 'desc' = 'asc';
  public defaultSortSide: 'client' | 'server' = 'server';
  public showOnTable = true;
  public showOnPdf = false;
  public showOnDetails = false;
  public showOnTotal = false;
  public showIconOnTable = false;
  public showIconOnHeader = false;
  public existOnTable: boolean;
  public formatter: any;
  public columnSize: Size;
  public specificWidth: number;
  public tableGroup: string;
  public columnBackgroundColor: string;
  public cellBackgroundColorField = '';
  public tableFilterType: TableFilterType = TableFilterType.text;
  public activeColumnTools = true;
  public highlightClickedCell = false;
  public defaultGroup = false;

  // Chart
  public backgroundColor: string;
  public borderColor: string;

  // Details view
  public showOnDetailsView: boolean;
  public detailsView: string; // Complete string format type|column|group|groupTitle
  public detailsViewType: DetailsViewType = DetailsViewType.oneLine;
  public detailsViewGroup: number;
  public detailsViewGroupTitle: string;
  public detailsViewColumn: number;
  public detailsIconValue: string;
  // public detailsDisplayOnTab = true;
  public expandableHeight = 200;

  // Indexed table
  public indexOrder = -1;

  // Form
  public showOnForm = true;
  public showOnCreate = true;
  public showOnUpdate = true;
  public showOnQuickForm = true;
  public disableOnCreate = false;
  public disableOnUpdate = false;
  public placeholder: string;
  public ellipseLabel = true;
  public alignOnForm = true;
  public formActions: StandardAction[];
  public tooltip = {
    content: '',
    type: '',
  };
  public mandatory = false;
  public editType: PropertyType;

  public position: number;
  public positionByContext: {
    table?: number;
    form?: number;
    detail?: number;
  } = {};
  public iconByContext: {
    table?: string;
    form?: string;
    detail?: string;
  } = {};

  get tablePosition(): number {
    return this.positionByContext.table !== undefined
      ? this.positionByContext.table
      : this.position;
  }
  set tablePosition(value: number) {
    this.positionByContext.table = value;
  }
  get formPosition(): number {
    return this.positionByContext.form !== undefined
      ? this.positionByContext.form
      : this.position;
  }
  set formPosition(value: number) {
    this.positionByContext.form = value;
  }
  get detailPosition(): number {
    return this.positionByContext.detail !== undefined
      ? this.positionByContext.detail
      : this.position;
  }
  set detailPosition(value: number) {
    this.positionByContext.detail = value;
  }

  get tableIcon(): string {
    return this.iconByContext.table !== undefined
      ? this.iconByContext.table
      : this.icon;
  }
  set tableIcon(value: string) {
    this.iconByContext.table = value;
  }
  get formIcon(): string {
    return this.iconByContext.form !== undefined
      ? this.iconByContext.form
      : this.icon;
  }
  set formIcon(value: string) {
    this.iconByContext.form = value;
  }
  get detailIcon(): string {
    return this.iconByContext.detail !== undefined
      ? this.iconByContext.detail
      : this.icon;
  }
  set detailIcon(value: string) {
    this.iconByContext.detail = value;
  }

  public formGroup: string;
  public autoFocus: boolean;

  public column: number;
  public width: number;
  public height: number; // signature
  public widthByContext: {
    desktopForm?: number;
    quickForm?: number;
    mobileForm?: number;
    details?: number;
    search?: number;
  } = {};

  public titleByContext: {
    form?: string;
    details?: string;
    search?: string;
    table?: string;
  } = {};
  public multiple: boolean;
  public underline = false;
  public enable = true;
  public enableRule: { field: string; value: any } = null;

  // FileInput & Gallery
  public gedList: string;
  public loadFileList = true;
  public fileKeyRequired = false;
  public ratioX = 192;
  public ratioY = 108;

  public default_value: any;
  // public items: NameValue[];
  public mask: Mask;
  public currency = 'EUR';
  public alias: string;
  public relation: RelationType;
  public relationField: string;
  public relationParam: string;
  public relationObject: Relation;
  public validation: Validation;
  public fullObject: boolean;

  // Text Input
  public textTransform: 'uppercase' | 'lowercase';
  public autocomplete = 'on';

  // Number input
  public warningMin: number;
  public warningMax: number;
  public unit: string;

  // Date input
  private _dateMin: Date;
  private _dateMax: Date;
  public withTime = true;

  public showWeekNumber: boolean;

  // Tva input
  public tvaSettings: TvaSettings;

  // Devise Input

  // Responsive
  public colXs: number;
  public colSm: number;
  public colMd: number;
  public colLg: number;
  private _responsiveClass: string;

  // Combobox
  public src: string;
  public ws: string;
  public wsKey = 'id';
  public wsLib = 'label';
  public wsTooltip = 'tooltip';
  public selectTitle: string;
  public data: any[];
  public insertUnknownValue = true;
  public showIconSelectedValue = false;
  public defaultIndex = -1;
  public tag: boolean;
  public clear = true;
  public colorField: any;
  public selectFirstValue = true;
  public selectAllOnLoad = false;

  public group: string;
  public groupLabel: string;

  public pictoItem;
  public searchInput = true;
  public sortItems = true;

  // Search Input
  public showZoom: boolean;
  public wsZoom: string;
  public urlZoomKey: string;
  public preloadList$: Observable<IEntity[]>;
  public zoomApi: string;
  public searchDetailsModel: string;
  public searchDetailsData: string;
  public canEmptySearch: boolean;
  public searchDetailsDisplay: 'list' | 'table' = 'list';
  public detailsOptions: DetailsOptions;
  public showDetailOptions = false;
  public httpFilter: HttpFilter;

  // SearchFilter
  public showOnSearch: boolean;
  public searchType: PropertyType;
  public searchTitle: string;
  public searchMultiple: boolean;
  public searchMandatory: boolean;
  public searchWidth: number;

  // Export
  public showOnExport: boolean;
  public replaceOnExport: string;

  // Preselection
  public showOnPreselection = false;

  // Edit and enum table
  public addLine = true;
  public removeLine = true;

  // Radio
  public showTitle = true;
  public isClosable = false;
  public wsItems: string;

  // Input Switch
  public reverse = false;

  // LabelEdit
  public labelOnTop = false;
  public selectAllOnFocus = true;

  // Input File
  public sendFileAuto = true;

  // API endpoint for server validation
  public checkByRequest: string;

  // Blur content
  public blurContent = false;

  public constructor(id: string, title?: string, type?: PropertyType) {
    // Initialization of default value
    this.id = id;
    this.title = title ?? '';
    this.type = type;
    this.editType = type;
    this.showOnTable = true;
    this.existOnTable = true;
    this.showOnExport = true;
    this.sortable = true;
    if (this.type === PropertyType.number) {
      this.align = 'right';
    }
    this.width = 100;
  }

  get responsiveClass(): string {
    let str =
      '' +
      (this.colXs !== undefined ? `col-xs-${this.colXs} ` : '') +
      (this.colSm !== undefined ? `col-sm-${this.colSm} ` : '') +
      (this.colMd !== undefined ? `col-md-${this.colMd} ` : '') +
      (this.colLg !== undefined ? `col-lg-${this.colLg} ` : '');

    if (str.length === 0) {
      str = 'col-sm-12';
    }
    return str;
  }

  public getPrimaryKey(): string {
    if (this.properties) {
      for (const attr in this.properties) {
        if (this.properties[attr].primaryKey) {
          return attr;
        }
      }
    }
    return undefined;
  }
  public getSchemaApi(): string {
    if (this.properties) {
      for (const attr in this.properties) {
        if (this.properties[attr].schemaApi) {
          return attr;
        }
      }
    }
    return undefined;
  }

  public getLabelKey(): string[] {
    const labelArray = [];
    if (this.properties) {
      for (const attr in this.properties) {
        if (this.properties[attr].mainLib) {
          labelArray.push(attr);
        }
      }
      return labelArray;
    }
    return undefined;
  }

  public hasLabelKey(): boolean {
    const keys = this.getLabelKey();
    return keys && keys.length > 0;
  }

  getColumnSize(): Size {
    if (this.columnSize) {
      return this.columnSize;
    } else {
      switch (this.type) {
        case PropertyType.boolean:
          return Size.XS;
        case PropertyType.time:
        case PropertyType.number:
          return Size.S;
        case PropertyType.date:
          return Size.M;
        case PropertyType.array:
          return Size.XL;
        default:
          return Size.L;
      }
    }
  }

  public toString(): string {
    return this.title;
  }

  /**
   * After object creation, init the property and build depency parameters (like enum/data)
   */
  public init() {
    if (this.src === 'enum') {
      if (!this.data) {
        if (this.getPrimaryKey() && this.getLabelKey().length > 0) {
          const id = this.properties[this.getPrimaryKey()];
          const label = this.properties[this.getLabelKey()[0]];
          if (
            id &&
            label &&
            Object.prototype.hasOwnProperty.call(id, 'enum') &&
            Object.prototype.hasOwnProperty.call(label, 'enum')
          ) {
            this.data = [];
            for (let i = 0; i < id.enum.length; ++i) {
              this.data.push(new MockEntity(id.enum[i], label.enum[i]));
            }
          }
        }
      } else {
        this.data = this.data.map((d) => {
          const id = d[this.wsKey ? this.wsKey : 'id'];
          const label = d[this.wsLib ? this.wsLib : 'label'];
          return new NameValue(id, label);
        });
      }
    }
    if (this.relation) {
      this.relationObject = new Relation(
        this.relation,
        this.relationField,
        this.relationParam,
      );
    }
    if (this.detailsView) {
      // Complete string format type|column|group|groupTitle
      this.showOnDetailsView = true;
      const split = this.detailsView.split('|');
      this.detailsViewType = split[0] as DetailsViewType;
      if (split.length > 1) {
        this.detailsViewColumn = parseInt(split[1], 10);
        if (split.length > 2) {
          this.detailsViewGroup = parseInt(split[2], 10);
          if (split.length > 3) {
            this.detailsViewGroupTitle = split[3];
          }
        }
      }
    }
    if (this._sortType === undefined) {
      this.setDefaultSort();
    }
  }

  public isAlias(type: Alias, name: string): boolean {
    return this.alias && this.alias.indexOf(`${type}=${name}`) >= 0;
  }

  getChartPropertyKey(): string {
    if (this.properties) {
      for (const p in this.properties) {
        if (this.properties[p].showOnChart) {
          return p;
        }
      }
    }
    return null;
  }

  getPositionByContext(filter: DataModelFilters): number {
    switch (filter) {
      case DataModelFilters.details:
        return this.detailPosition;
      case DataModelFilters.table:
        return this.tablePosition;
      case DataModelFilters.form:
      case DataModelFilters.formCreation:
      case DataModelFilters.formUpdate:
      case DataModelFilters.quickFormCreation:
      case DataModelFilters.quickFormUpdate:
        return this.formPosition;
      default:
        return undefined;
    }
  }

  /**
   * get label of any object if property has label keys
   */
  public convertToLabelValue(value: any): string {
    let str = '';
    if (this.hasLabelKey()) {
      for (const l of this.getLabelKey()) {
        if (value[l]) {
          str += value[l];
        }
      }
    }
    return str;
  }

  public serialize(): any {
    const serial: any = {};
    for (const attr in this) {
      if (Object.prototype.hasOwnProperty.call(this, attr)) {
        serial[attr] = this[attr];
      }
    }
    return serial;
  }

  private setDefaultSort() {
    switch (this.type) {
      case PropertyType.number:
        this._sortType = SortType.number;
        break;
      case PropertyType.date:
        this._sortType = SortType.date;
        break;
      case PropertyType.time:
        this._sortType = SortType.time;
        break;
      case PropertyType.dateTime:
        this._sortType = SortType.dateTime;
        break;
      default:
        this._sortType = SortType.string;
        break;
    }
  }
}
